'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface SignUpValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpValues>();

  const password = watch('password');

  const onSubmit = async (data: SignUpValues) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/register with user data
    await new Promise((r) => setTimeout(r, 1800));
    toast.success('Account created! Welcome to DetailBook.');
    setIsLoading(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-700 text-foreground">Create your account</h1>
        <p className="text-muted-foreground text-sm mt-1">Start booking in under a minute</p>
      </div>

      {/* Google OAuth */}
      <button className="btn-secondary w-full mb-4 flex items-center gap-3 justify-center">
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Sign up with Google
      </button>

      <div className="flex items-center gap-3 mb-4">
        <hr className="flex-1 border-border" />
        <span className="text-xs text-muted-foreground font-500">or with email</span>
        <hr className="flex-1 border-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-600 text-foreground mb-1.5">First Name <span className="text-red-500">*</span></label>
            <input
              {...register('firstName', { required: 'Required' })}
              placeholder="Priya"
              className={`input-field ${errors.firstName ? 'error' : ''}`}
            />
            {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-600 text-foreground mb-1.5">Last Name <span className="text-red-500">*</span></label>
            <input
              {...register('lastName', { required: 'Required' })}
              placeholder="Sharma"
              className={`input-field ${errors.lastName ? 'error' : ''}`}
            />
            {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Email Address <span className="text-red-500">*</span></label>
          <input
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
            placeholder="priya@gmail.com"
            className={`input-field ${errors.email ? 'error' : ''}`}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">
            Password <span className="text-red-500">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">At least 8 characters with one number or symbol.</p>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'At least 8 characters required' },
                pattern: { value: /(?=.*[0-9!@#$%^&*])/, message: 'Include at least one number or symbol' },
              })}
              placeholder="••••••••"
              className={`input-field pr-10 ${errors.password ? 'error' : ''}`}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-xs font-600 text-foreground mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (v) => v === password || 'Passwords do not match',
              })}
              placeholder="••••••••"
              className={`input-field pr-10 ${errors.confirmPassword ? 'error' : ''}`}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showConfirm ? 'Hide password' : 'Show password'}>
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
        </div>

        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            id="agreeTerms"
            {...register('agreeTerms', { required: 'You must agree to continue' })}
            className="w-4 h-4 mt-0.5 rounded border-border text-primary focus:ring-primary flex-shrink-0"
          />
          <label htmlFor="agreeTerms" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
            I agree to the{' '}
            <a href="#" className="text-primary font-600 hover:underline">Terms of Service</a>{' '}
            and{' '}
            <a href="#" className="text-primary font-600 hover:underline">Privacy Policy</a>
          </label>
        </div>
        {errors.agreeTerms && <p className="text-red-500 text-xs -mt-2">{errors.agreeTerms.message}</p>}

        <button type="submit" disabled={isLoading} className="btn-primary w-full mt-1">
          {isLoading ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Creating account...
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      <p className="text-sm text-center text-muted-foreground mt-4">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-600 hover:underline">
          Sign in
        </button>
      </p>
    </div>
  );
}